// src/pages/PatientMessagesInboxPage.jsx
//
// Lists PATIENT_DOCTOR (for a doctor) or PATIENT_NAVIGATOR (for a navigator)
// conversations for the currently logged-in staff member — the visibility
// gap TeamInboxPage.jsx doesn't cover. Until now the ONLY way to reach a
// patient-initiated conversation was to already be on that exact patient's
// own detail page and click "Chat" — there was nowhere to discover that a
// patient had started one without already knowing which patient to check.
//
// 18 Jul 2026: added a "+ New message" picker so staff can initiate too, not
// just receive — previously a navigator/doctor could only ever reply to a
// patient-started thread, never start one from here. Two DIFFERENT picker
// UIs depending on role, because the two patient sets are different scales:
//   DOCTOR    → NewConversationPicker (flat list) — bounded set, only
//               patients with a real appointment (same eligibility rule
//               enforced everywhere else in this feature).
//   NAVIGATOR → PatientSearchPicker (type-ahead search) — unrestricted, and
//               potentially hundreds of patients per navigator (per the
//               hiring-ratio docs), so a flat list doesn't scale here.
//               Reuses the EXISTING GET /api/patients/search endpoint.

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AppShell } from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { chatMessagingAPI } from '../services/chatMessagingAPI';
import { useHumanChat } from '../hooks/useHumanChat';
import HumanChatWindow from '../components/chat/HumanChatWindow';
import NewConversationPicker from '../components/chat/NewConversationPicker';
import PatientSearchPicker from '../components/chat/PatientSearchPicker';
import avatarTint from '../utils/avatarTint';
import Icon from '../components/Icon';

function initials(name) {
  if (!name) return '?';
  return name.trim().split(/\s+/).map(w => w[0]).slice(0, 2).join('').toUpperCase();
}

function formatWhen(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const today = new Date();
  if (d.toDateString() === today.toDateString()) {
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
}

export default function PatientMessagesInboxPage() {
  const { user } = useAuth();
  const { toast } = useToast();
  const isDoctor = user?.role === 'DOCTOR';
  const relevantType = isDoctor ? 'PATIENT_DOCTOR' : 'PATIENT_NAVIGATOR';

  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [searchParams] = useSearchParams();

  const loadConversations = useCallback(() => {
    setLoading(true);
    chatMessagingAPI.list()
      .then((all) => setConversations(all.filter(c => c.conversationType === relevantType)))
      .catch((e) => setError(e.message || 'Could not load patient messages.'))
      .finally(() => setLoading(false));
  }, [relevantType]);

  useEffect(() => { loadConversations(); }, [loadConversations]);

  // autoSelectedRef also guards the deep-link effect right below — see
  // PatientCareTeamPage.jsx, which has the identical pair.
  const autoSelectedRef = useRef(false);

  // Deep-link from the notification bell (?conversationId=...) — see
  // useNotifications.js. Without this, every "N new messages" notification
  // for this role pointed at this page in general, so clicking one just
  // opened whichever conversation the auto-select effect below had already
  // picked — not the one the notification named.
  useEffect(() => {
    const cid = searchParams.get('conversationId');
    if (!cid) return;
    setActiveId(cid);
    autoSelectedRef.current = true;
  }, [searchParams]);

  // Auto-select the most recently active thread, same reasoning as
  // PatientCareTeamPage.jsx / TeamInboxPage.jsx — otherwise the right panel
  // sits empty even when there's an obvious most-recent conversation, and
  // this is exactly the page meant to surface "did a patient message me."
  //
  // 18 Jul 2026 fix: only fire once, on initial load (autoSelectedRef) —
  // this used to re-fire and re-select the same conversation every time
  // activeId became null, including when the mobile "← Back" button
  // explicitly cleared it to return to the list. Invisible on desktop
  // (never sets activeId to null in normal use), but on mobile it meant
  // Back appeared to do nothing.
  useEffect(() => {
    if (autoSelectedRef.current) return;
    if (activeId || conversations.length === 0) return;
    const mostRecent = conversations.reduce((latest, c) => {
      const cTime = new Date(c.lastMessageAt || c.createdAt).getTime();
      const latestTime = new Date(latest.lastMessageAt || latest.createdAt).getTime();
      return cTime > latestTime ? c : latest;
    });
    setActiveId(mostRecent.id);
    autoSelectedRef.current = true;
  }, [conversations, activeId]);

  const active = conversations.find(c => c.id === activeId) || null;
  const chat = useHumanChat(activeId);

  const unreadTotal = conversations.reduce((sum, c) => sum + (c.unreadCount || 0), 0);

  // Both pickers resolve to the same shape: { id, name } for whoever was
  // picked. NewConversationPicker (doctor) already returns that shape
  // (EligibleCounterpart); PatientSearchPicker normalizes to match it too,
  // so this one handler works for both.
  async function handlePickPatient(patient) {
    try {
      const conversation = await chatMessagingAPI.createOrGet(relevantType, patient.id);
      setPickerOpen(false);
      loadConversations();
      setActiveId(conversation.id);
    } catch (e) {
      toast.error(e.message || 'Could not start that conversation.');
    }
  }

  return (
    <AppShell breadcrumbs={[{ label: 'Patient Messages' }]}>
      <style>{`
        @media (max-width: 680px) {
          .chatinbox-wrap { gap: 0 !important; }
          .chatinbox-wrap.has-active .chatinbox-list { display: none !important; }
          .chatinbox-wrap:not(.has-active) .chatinbox-detail { display: none !important; }
          .chatinbox-list, .chatinbox-detail { width: 100% !important; border-radius: 0 !important; }
          .chatinbox-back-btn { display: inline-flex !important; }
        }
      `}</style>
      <div className={`chat-pane chatinbox-wrap${activeId ? ' has-active' : ''}`}>
        {/* Thread list */}
        <div className="chatinbox-list panel" style={{
          width: 280, flexShrink: 0, overflowY: 'auto',
        }}>
          <div className="panel-head" style={{ alignItems: 'flex-start' }}>
            <div>
              <div style={{ fontFamily: 'var(--font-display)', fontSize: 16, color: 'var(--text-primary)' }}>
                Patient Messages
              </div>
              {unreadTotal > 0 && (
                <div style={{ fontSize: 11, color: 'var(--brand-accent)', marginTop: 2 }}>
                  {unreadTotal} unread
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={() => setPickerOpen(true)}
              title="New message"
              aria-label="New message"
              className="icon-btn-round"
              style={{ fontSize: 14, flexShrink: 0 }}
            >
              <Icon name="plus" size={14} />
            </button>
          </div>

          {loading && <div style={{ padding: 16, fontSize: 13, color: 'var(--text-muted)' }}>Loading…</div>}
          {error && <div style={{ padding: 16, fontSize: 13, color: 'var(--red)' }}>{error}</div>}
          {!loading && !error && conversations.length === 0 && (
            <div style={{ padding: 16, fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.6 }}>
              No patient conversations yet. When a patient messages you, or you
              start one from their profile, it'll show up here.
            </div>
          )}

          {conversations.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => setActiveId(c.id)}
              className={`panel-row${c.id === activeId ? ' active' : ''}`}
            >
              <div className={`avatar avatar--sm ${avatarTint(c.counterpartName)}`} aria-hidden="true">
                {initials(c.counterpartName)}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  fontSize: 13, fontWeight: 600, color: 'var(--text-primary)',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                  {c.counterpartName}
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Patient</div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
                <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>{formatWhen(c.lastMessageAt)}</span>
                {c.unreadCount > 0 && (
                  <span className="tab-count" style={{ marginLeft: 0 }}>
                    {c.unreadCount}
                  </span>
                )}
              </div>
            </button>
          ))}
        </div>

        {/* Active thread */}
        <div className="chatinbox-detail" style={{ flex: 1, minWidth: 0, minHeight: 0 }}>
          {active ? (
            <HumanChatWindow
              counterpartName={active.counterpartName}
              counterpartRole={active.counterpartRole}
              counterpartId={active.counterpartId}
              messages={chat.messages}
              loading={chat.loading}
              error={chat.error}
              sending={chat.sending}
              onSend={chat.sendMessage}
              onBack={() => setActiveId(null)}
            />
          ) : (
            <div className="panel" style={{
              height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: 'var(--text-muted)', fontSize: 13,
            }}>
              Select a conversation to view messages.
            </div>
          )}
        </div>
      </div>

      {pickerOpen && isDoctor && (
        <NewConversationPicker
          conversationType="PATIENT_DOCTOR"
          title="Message a patient"
          onSelect={handlePickPatient}
          onClose={() => setPickerOpen(false)}
        />
      )}
      {pickerOpen && !isDoctor && (
        <PatientSearchPicker
          onSelect={handlePickPatient}
          onClose={() => setPickerOpen(false)}
        />
      )}
    </AppShell>
  );
}
